import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Share2, Copy, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { buildShareGrid } from "@/lib/storage";

export default function ResultsModal({ open, onOpenChange, result, rows, date, specialty, stats }) {
  if (!result) return null;
  const { correct, correct_diagnosis, explanation, clinical_pearls } = result;
  const grid = rows
    .map((r) => (r.relevant === true ? "🟩" : "🟥"))
    .concat(Array(5 - rows.length).fill("⬜"))
    .join("");

  const share = () => {
    const text = buildShareGrid({
      rows,
      won: correct,
      date,
      specialty,
      streak: stats?.current_streak ?? 0,
    });
    if (navigator.share) {
      navigator.share({ text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    }
  };

  const copy = () => {
    const text = buildShareGrid({
      rows,
      won: correct,
      date,
      specialty,
      streak: stats?.current_streak ?? 0,
    });
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="results-modal"
        className="bg-white border border-slate-200 rounded-2xl w-[calc(100%-1rem)] max-w-lg p-0 overflow-hidden flex flex-col max-h-[92dvh] sm:max-h-[85vh]"
      >
        <div className={`shrink-0 px-5 sm:px-6 py-4 sm:py-5 ${correct ? "bg-emerald-500" : "bg-slate-900"}`}>
          <button
            data-testid="results-back-btn"
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-1.5 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur-sm text-white text-[11px] font-mono tracking-[0.15em] uppercase px-3 py-1.5 mb-3 active:scale-95 transition-all"
            aria-label="Back to case"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to case
          </button>
          <DialogHeader>
            <p className="text-white/70 text-[11px] font-mono tracking-[0.2em] uppercase">
              {correct ? "Diagnosis confirmed" : "Case closed"}
            </p>
            <DialogTitle
              className="text-white text-xl sm:text-2xl font-bold tracking-tight leading-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {correct ? "You solved it." : "Not quite."}
            </DialogTitle>
          </DialogHeader>
          <p className="text-white/90 text-sm mt-2 font-mono tracking-[0.2em] sm:tracking-[0.35em] break-all">{grid}</p>
        </div>

        <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-4 sm:py-5 space-y-4">
          <div>
            <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
              Correct diagnosis
            </p>
            <p
              className="text-base sm:text-lg font-semibold text-slate-900 mt-1 leading-snug"
              style={{ fontFamily: "Outfit, sans-serif" }}
              data-testid="results-diagnosis"
            >
              {correct_diagnosis}
            </p>
          </div>

          {explanation && (
            <div>
              <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
                Why this diagnosis
              </p>
              <p className="text-sm text-slate-700 leading-relaxed mt-1">{explanation}</p>
            </div>
          )}

          {clinical_pearls?.length > 0 && (
            <div>
              <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
                Clinical pearls
              </p>
              <ul className="mt-2 space-y-1.5">
                {clinical_pearls.map((p) => (
                  <li key={p} className="text-sm text-slate-700 leading-relaxed flex gap-2">
                    <span className="text-emerald-500 shrink-0">◆</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {stats && (
            <div className="grid grid-cols-4 gap-2 sm:gap-3 pt-2">
              {[
                { label: "Played", value: stats.played },
                { label: "Win %", value: stats.played ? Math.round((stats.wins * 100) / stats.played) : 0 },
                { label: "Current", value: stats.current_streak },
                { label: "Best", value: stats.max_streak },
              ].map((m) => (
                <div key={m.label} className="rounded-xl bg-slate-50 border border-slate-200 px-1 sm:px-2 py-2.5 sm:py-3 text-center">
                  <p
                    className="text-xl sm:text-2xl font-bold text-slate-900 font-mono"
                    data-testid={`stat-${m.label.toLowerCase().replace(" ", "-").replace("%", "pct")}`}
                  >
                    {m.value}
                  </p>
                  <p className="text-[10px] font-mono tracking-[0.1em] sm:tracking-[0.15em] uppercase text-slate-500 mt-1">
                    {m.label}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-slate-100 px-5 sm:px-6 py-3 sm:py-4 flex gap-2 bg-white">
          <button
            data-testid="share-btn"
            onClick={share}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 text-white px-5 py-2.5 sm:py-3 text-sm font-medium hover:-translate-y-0.5 active:scale-95 transition-all"
          >
            <Share2 className="h-4 w-4" /> Share
          </button>
          <button
            data-testid="copy-btn"
            onClick={copy}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 sm:py-3 text-sm font-medium text-slate-800 hover:border-slate-500 active:scale-95 transition-all"
          >
            <Copy className="h-4 w-4" /> Copy
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
